import type { StateCreator } from 'zustand';
import type { ProjectClosureReport, LessonLearned, Recommendation } from '../../types/closure';

// ============================================================================
// CLOSURE SLICE - Manages project closure reports and lessons learned
// ============================================================================

export interface ClosureSlice {
  // State
  currentReport: ProjectClosureReport | null;
  lessonsLearned: LessonLearned[];
  customRecommendations: Recommendation[];
  isGeneratingReport: boolean;

  // Report generation
  setCurrentReport: (report: ProjectClosureReport | null) => void;
  setGeneratingReport: (isGenerating: boolean) => void;

  // Lessons learned management
  addLessonLearned: (lesson: Omit<LessonLearned, 'id' | 'addedAt'>) => void;
  updateLessonLearned: (id: string, updates: Partial<LessonLearned>) => void;
  deleteLessonLearned: (id: string) => void;
  clearLessonsLearned: () => void;

  // Recommendations management
  addRecommendation: (recommendation: Omit<Recommendation, 'id'>) => void;
  updateRecommendation: (id: string, updates: Partial<Recommendation>) => void;
  deleteRecommendation: (id: string) => void;
  clearRecommendations: () => void;

  // Utilities
  getLessonsLearnedByCategory: (category: LessonLearned['category']) => LessonLearned[];
  getRecommendationsByPriority: (priority: Recommendation['priority']) => Recommendation[];
}

export const createClosureSlice: StateCreator<ClosureSlice> = (set, get) => {
  const canEdit = (): boolean => {
    const state = get() as any;
    const role = state.currentProject?.currentUserRole;
    if (role) {
      const allowed = role === 'OWNER' || role === 'ADMIN' || role === 'EDITOR';
      if (!allowed) {
        state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      }
      return allowed;
    }
    if (state.storageMode === 'backend') {
      state.addNotification?.('warning', 'Droits insuffisants pour modifier ce contenu.');
      return false;
    }
    return true;
  };

  return ({
  // Initial state
  currentReport: null,
  lessonsLearned: [],
  customRecommendations: [],
  isGeneratingReport: false,

  // Report generation
  setCurrentReport: (report) => set({ currentReport: report }),
  setGeneratingReport: (isGenerating) => set({ isGeneratingReport: isGenerating }),

  // Lessons learned management
  addLessonLearned: (lesson) => {
    if (!canEdit()) return;
    const newLesson: LessonLearned = {
      ...lesson,
      id: `lesson-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      addedAt: new Date(),
    };

    set((state) => ({
      lessonsLearned: [...state.lessonsLearned, newLesson],
    }));
  },

  updateLessonLearned: (id, updates) => {
    if (!canEdit()) return;
    set((state) => ({
      lessonsLearned: state.lessonsLearned.map((lesson) =>
        lesson.id === id ? { ...lesson, ...updates } : lesson
      ),
    }));
  },

  deleteLessonLearned: (id) => {
    if (!canEdit()) return;
    set((state) => ({
      lessonsLearned: state.lessonsLearned.filter((lesson) => lesson.id !== id),
    }));
  },

  clearLessonsLearned: () => {
    if (!canEdit()) return;
    set({ lessonsLearned: [] });
  },

  // Recommendations management
  addRecommendation: (recommendation) => {
    if (!canEdit()) return;
    const newRecommendation: Recommendation = {
      ...recommendation,
      id: `rec-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };

    set((state) => ({
      customRecommendations: [...state.customRecommendations, newRecommendation],
    }));
  },

  updateRecommendation: (id, updates) => {
    if (!canEdit()) return;
    set((state) => ({
      customRecommendations: state.customRecommendations.map((rec) =>
        rec.id === id ? { ...rec, ...updates } : rec
      ),
    }));
  },

  deleteRecommendation: (id) => {
    if (!canEdit()) return;
    set((state) => ({
      customRecommendations: state.customRecommendations.filter((rec) => rec.id !== id),
    }));
  },

  clearRecommendations: () => {
    if (!canEdit()) return;
    set({ customRecommendations: [] });
  },

  // Utilities
  getLessonsLearnedByCategory: (category) => {
    return get().lessonsLearned.filter((lesson) => lesson.category === category);
  },

  getRecommendationsByPriority: (priority) => {
    return get().customRecommendations.filter((rec) => rec.priority === priority);
  },
  });
};
